'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import slugify from 'slugify'
import { useState, useEffect, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { useQuery } from '@tanstack/react-query'
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import { MagnifyingGlassIcon, ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/20/solid'

import { Api, Vehicle } from '@cruz-com/lookup'
import { AutoComplete, Button, Checkbox, Radio, Toggle, ListBox, PageLoading, Text } from '@/components'
import { vehicleApi } from '@/api'
import { Section } from './vehicleFilterSection'
import { MakeModeTrim } from './vehicleMakeModelTrim'
import { radiusOption } from '@/settings'
import { useGlobalStore } from '@/store/provider'

type Props = {
  category?: 'electric' | 'phev' | 'exotic' | 'luxury' | 'sport'
  | 'heavyHauler' | 'offRoad' | 'convertible' | 'fuelEfficient' | 'family' | 'private'
  | 'antique' | 'classic' | 'cheap' | 'wheelchairAccessible' | 'certified'
  bodyTypeId?: number | string
  ignoreFilters?: string[]
  defaultQuery: Vehicle.Query
  onFilterChanged: (query: Vehicle.Query) => void
}

const groupStyle = 'w-full divide-y border rounded-md bg-zinc-100/80 dark:bg-zinc-800/80'

export function VehicleFilter1({ category, bodyTypeId, ignoreFilters, defaultQuery, onFilterChanged }: Props) {
  const pathname = usePathname()
  const { scrollTopOver } = useGlobalStore((state) => state)
  const [showFilter, setShowFilter] = useState(false)
  const [query, setQuery] = useState<Vehicle.Query | any>(defaultQuery)
  const [loc, setLoc] = useState<any>(_.pick(defaultQuery, ['zip_code', 'radius']))

  useEffect(() => {
    setTimeout(() => {
      setQuery(defaultQuery)
      setLoc(_.pick(defaultQuery, ['zip_code', 'radius']))
    }, 50)
  }, [defaultQuery])

  function getQuery() {
    if (bodyTypeId) {
      return _.pickBy({ ...query, body_type: bodyTypeId }, _.identity)
    }
    return _.pickBy(query, _.identity)
  }

  // get options
  const { data, isFetching, refetch } = useQuery<Api.Result<Vehicle.FilterOptions> | any>({
    queryKey: [`vehicleFilterOptions_${category || 'none'}`],
    queryFn: () => vehicleApi.getFilterOption(getQuery(), category),
    staleTime: 3000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false
  })

  // checkbox changed - multiple selection
  function checkboxChanged({ key, id, val }: { key: string, id: number, val: boolean }) {
    const x: string[] = query[key] ? _.split(query[key] as string, ',') : []
    const y = val ? _.concat(x, `${id}`) : _.without(x, `${id}`)
    if (_.isEmpty(y)) {
      return setQuery(_.omit(query, key))
    }
    setQuery({ ...query, [key]: _.join(y, ',') })
  }

  // (dropdown or radio) changed - single selection
  function radioChanged({ name, id }: { name: string, id: number }) {
    return setQuery({ ...query, [name]: id })
  }

  // toggle (switch) changed
  function toggleChanged(name: string, isEnabled: boolean) {
    if (isEnabled) {
      return setQuery({ ...query, [name]: 1 })
    }
    setQuery(_.omit(query, name))
  }

  // mmt (make, model, trim) changed
  function mmtChanged(name: string, val: string | null | any) {
    if (!_.get(query, name) && !val) { return }
    if (_.isEmpty(val)) {
      return setQuery(_.omit(query, name))
    }
    setQuery({ ...query, [name]: val })
  }

  // ---- location ----
  function locChanged(name: string, value: any) {
    let updateValue = value
    if (name === 'zip_code') {
      updateValue = value.replace(/\D/g, '') // Remove all non-digit characters
    }
    const d = { ...loc, [name]: updateValue }
    const zLen = _.size(d.zip_code)
    setLoc(d)
    if (zLen === 5 || (zLen === 0 && _.size(loc.zip_code) > 0)) {
      if (d.radius > 0 || (d.radius < 0 && loc.radius > 0)) {
        setQuery({ ...query, ...d })
      }
    }
  }

  // apply filter
  function applyFilter() {
    setShowFilter(false)
    _.keys(query).forEach((key) => {
      (query[key] < 0) && delete query[key]
    })
    setTimeout(() => {
      onFilterChanged(query)
    }, 200)
  }

  // default query changed (reset query)
  useEffect(() => {
    if (_.isEmpty(defaultQuery) && !_.isEmpty(query)) {
      setQuery({})
    }
  }, [defaultQuery])

  // query changed - get new options
  useEffect(() => {
    refetch()
  }, [query])

  // data changed (fetched) - set query
  useEffect(() => {
    // remove unavailable filter options
    const qKeys = _.pull(_.keys(query), 'page_number', 'page_size', 'sort_id', 'sort_value')
    _.forEach(qKeys, (key) => {
      const lkp = _.get(data?.data, key)
      if (!lkp) {
        return
      }
      const q = _.split(query[key], ',')
      const nq: any[] = []
      _.forEach(q, (id) => {
        let x = _.find(lkp, d => { return d?.id?.toString() === id?.toString() })
        if (x) { nq.push(id) }
      })
      if (nq.length > 0) {
        _.assign(query, { [key]: _.join(nq, ',') })
      }
    })
  }, [data?.data])

  // options
  const {
    condition, body_type, sale_status, price, mileage, year_min, year_max, make,
    // powertrain
    mpg, drive_type, fuel_type, trans_type, trans_gears, towing_capacity,
    engine_cylinders, engine_aspiration, engine_displacement, acceleration, horsepower, torque,
    // specifications
    exterior_color, interior_color, upholstery,
    doors, max_seating,
    bed_length,
    size,
    // other
    feature, country, seller
  } = data?.data || {}

  const BodyType = () =>
    ignoreFilters?.includes('body_type') ? <></> :
      <div>
        <div className='filter-label mb-2'>Body Style</div>
        <ul className='grid grid-cols-2 sm:grid-cols-3 gap-x-3 w-full'>
          {_.map(body_type, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`body_type_${id}`} label={name}
                checked={_.split(query?.body_type, ',').includes(id?.toString())}
                onChange={(val) => checkboxChanged({ key: 'body_type', id, val })}
              />
            </li>)}
        </ul>
      </div>

  const yearOption = useCallback(() => {
    if (!year_min || !year_max) {
      return [{ id: -1, name: 'Any' }]
    }
    const d = _.map(_.range(year_max, (year_min || 1901) - 1), (y) => {
      return { id: y, name: y.toString() }
    })
    return [{ id: -1, name: 'Any' }, ...d]
  }, [year_min, year_max])

  const Condition = () =>
    ignoreFilters?.includes('condition') ? <></> :
      <div>
        <div className='filter-label mb-2'>Condition</div>
        <ul className='flex flex-col sm:flex-row gap-x-3'>
          {_.map(condition, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`condition_${id}`} label={name}
                checked={_.split(query?.condition, ',').includes(id?.toString())}
                onChange={(val) => checkboxChanged({ key: 'condition', id, val })}
              />
            </li>)}
        </ul>
      </div>

  const SaleStatus = () =>
    ignoreFilters?.includes('sale_status') ? <></> :
      <div>
        <div className='filter-label mb-1'>Sale Status</div>
        <ul className='flex flex-col sm:flex-row gap-x-3 gap-y-1'>
          {_.map(sale_status, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`sale_status_id_${id}`} label={name}
                checked={_.split(query?.sale_status, ',').includes(id?.toString())}
                onChange={(val) => checkboxChanged({ key: 'sale_status', id, val })}
              />
            </li>)}
        </ul>
      </div>

  const Year = () =>
    <div>
      <div className='filter-label mb-2'>Year</div>
      <div className='w-fit flex items-end gap-x-3'>
        <div className='w-28'>
          <ListBox compact={true} option={yearOption()} value={query?.year_min || -1} className='w-32'
            onChange={(val) => { radioChanged({ name: 'year_min', id: val as number }) }}
          />
        </div>
        <div className='text-sm/8'>to</div>
        <div className='w-28'>
          <ListBox compact={true} option={yearOption()} value={query?.year_max || -1} className='w-32'
            onChange={(val) => { radioChanged({ name: 'year_max', id: val as number }) }}
          />
        </div>
      </div>
    </div>

  const Price = () =>
    ignoreFilters?.includes('price') ? <></> :
      <div>
        <div className='filter-label mb-1'>Price</div>
        <div className='w-fit flex items-end gap-x-3'>
          <div className='w-36'>
            <ListBox compact={true} option={price}
              value={query?.price_min || -1} className='w-32'
              onChange={(val) => { radioChanged({ name: 'price_min', id: val as number }) }}
            />
          </div>
          <div className='text-sm/8'>to</div>
          <div className='w-36'>
            <ListBox compact={true} option={price}
              value={query?.price_max || -1} className='w-32'
              onChange={(val) => { radioChanged({ name: 'price_max', id: val as number }) }}
            />
          </div>
        </div>
      </div>

  const Mileage = () =>
    <div className='w-64'>
      <div className='filter-label mb-2'>Mileage <span className='text-sm font-light'>(less than)</span></div>
      <ListBox compact={true} option={mileage} value={query?.mileage || -1}
        disabled={_.size(mileage) < 2}
        onChange={(val) => { radioChanged({ name: 'mileage', id: val as number }) }}
      />
    </div>

  const DriveType = () =>
    ignoreFilters?.includes('drive_type') ? <></> :
      <Section name='driveType' title='Drive Type' hasValue={query?.drive_type}>
        {(!isFetching && _.isEmpty(drive_type)) && <NoOption />}
        {!_.isEmpty(drive_type) &&
          <ul className='grid grid-cols-2 sm:grid-cols-3 gap-x-2'>
            {_.map(drive_type, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`drive_type_${id}`} label={name}
                  checked={_.split(query?.drive_type, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'drive_type', id, val })}
                />
              </li>)}
          </ul>}
      </Section>

  const FuelEconomy = () =>
    ignoreFilters?.includes('mpg') ? <></> :
      <Section name='mpg' title='Fuel Economy' description='mpg' hasValue={(query?.mpg || 0) > 0}>
        {(!isFetching && _.isEmpty(mpg)) && <NoOption />}
        {!_.isEmpty(mpg) &&
          <ul className='grid grid-cols-3 sm:grid-cols-6 gap-x-2'>
            {_.map(mpg, ({ id, name }) =>
              <li key={id}>
                <Radio name='mpg' id={id} label={name}
                  checked={query?.mpg?.toString() === id.toString()}
                  onChange={() => radioChanged({ name: 'mpg', id })} />
              </li>
            )}
          </ul>}
      </Section>

  const FuelType = () =>
    ignoreFilters?.includes('fuel_type') ? <></> :
      <Section name='fuelType' title='Fuel Type' hasValue={query?.fuel_type}>
        {(!isFetching && _.isEmpty(fuel_type)) && <NoOption />}
        {!_.isEmpty(fuel_type) &&
          <ul className='grid grid-cols-1 sm:grid-cols-3 gap-x-2'>
            {_.map(fuel_type, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`fuel_type_${id}`} label={name}
                  checked={_.split(query?.fuel_type, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'fuel_type', id, val })} />
              </li>)}
          </ul>}
      </Section>

  const TransType = () =>
    ignoreFilters?.includes('trans_type') ? <></> :
      <Section name='transType' title='Transmission Type' hasValue={query?.trans_type}>
        {(!isFetching && _.isEmpty(trans_type)) && <NoOption />}
        {!_.isEmpty(trans_type) &&
          <ul className='grid grid-cols-1 sm:grid-cols-3 gap-x-2'>
            {_.map(trans_type, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`trans_type_${id}`} label={name}
                  checked={_.split(query?.trans_type, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'trans_type', id, val })} />
              </li>)}
          </ul>}
      </Section>

  const TransGears = () =>
    ignoreFilters?.includes('trans_gears') ? <></> :
      <Section name='transGears' title='Transmission Gears' hasValue={query?.trans_gears}>
        {(!isFetching && _.isEmpty(trans_gears)) && <NoOption />}
        {!_.isEmpty(trans_gears) &&
          <ul className='grid grid-cols-4 sm:grid-cols-6 gap-x-2'>
            {_.map(trans_gears, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`trans_gears_${id}`} label={name}
                  checked={_.split(query?.trans_gears, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'trans_gears', id, val })} />
              </li>)}
          </ul>}
      </Section>

  const EngineAspiration = () =>
    ignoreFilters?.includes('engine_aspiration') ? <></> :
      <Section name='engAspiration' title='Engine Aspiration' hasValue={query?.engine_aspiration}>
        {(!isFetching && _.isEmpty(engine_aspiration)) && <NoOption />}
        {!_.isEmpty(engine_aspiration) &&
          <ul className='grid grid-cols-1 sm:grid-cols-3 gap-x-2'>
            {_.map(engine_aspiration, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`eng_aspiration_${id}`} label={name}
                  checked={_.split(query?.engine_aspiration, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'engine_aspiration', id, val })} />
              </li>)}
          </ul>}
      </Section>

  const EngineCylinder = () =>
    ignoreFilters?.includes('engine_cylinders') ? <></> :
      <Section name='engCylinder' title='Engine Cylinders' hasValue={query?.engine_cylinders}>
        {(!isFetching && _.isEmpty(engine_cylinders)) && <NoOption />}
        {!_.isEmpty(engine_cylinders) &&
          <ul className='grid grid-cols-3 sm:grid-cols-6 gap-x-2'>
            {_.map(engine_cylinders, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`eng_cylinder_${id}`} label={name}
                  checked={_.split(query?.engine_cylinders, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'engine_cylinders', id, val })} />
              </li>)}
          </ul>}
      </Section>

  const EngineDisplacement = () =>
    ignoreFilters?.includes('engine_displacement') ? <></> :
      <Section name='engDisplacement' title='Engine Displacement' hasValue={query?.engine_displacement}>
        <div className='flex items-center'>
          <AutoComplete name='engine_displacement' inputMode='tel' className='w-20'
            option={engine_displacement}
            value={query?.engine_displacement?.toString()}
            onChange={(val) => { radioChanged({ name: 'engine_displacement', id: val?.toString() }) }}
          />
          <span className='pl-2 text-sm'>liters</span>
        </div>
      </Section>

  const TowingCapacity = () =>
    ignoreFilters?.includes('towing_capacity') ? <></> :
      <Section name='towing_capacity' title='Standard Towing Capacity' description='lbs' hasValue={(query?.towing_capacity || 0) > 0}>
        {(!isFetching && _.isEmpty(towing_capacity)) && <NoOption />}
        <ul className='grid grid-cols-3 sm:grid-cols-4 gap-x-2'>
          {_.map(towing_capacity, ({ id, name }) =>
            <li key={id}>
              <Radio name='towing_capacity' id={id} label={name}
                checked={query?.towing_capacity?.toString() === id.toString()}
                onChange={() => radioChanged({ name: 'towing_capacity', id })} />
            </li>)}
        </ul>
      </Section >

  const Acceleration = () =>
    ignoreFilters?.includes('acceleration') ? <></> :
      <Section name='acceleration' title='Acceleration' description='0-60 mph, in sec.' hasValue={(query?.acceleration || 0) > 0}>
        {(!isFetching && _.isEmpty(acceleration)) && <NoOption />}
        {!_.isEmpty(acceleration) &&
          <ul className='grid grid-cols-3 sm:grid-cols-6 gap-x-2'>
            {_.map(acceleration, ({ id, name }) =>
              <li key={id}>
                <Radio name='acceleration' id={id} label={name}
                  checked={query?.acceleration?.toString() === id.toString()}
                  onChange={() => radioChanged({ name: 'acceleration', id })} />
              </li>)}
          </ul>}
      </Section>

  const Horsepower = () =>
    ignoreFilters?.includes('horsepower') ? <></> :
      <Section name='horsepower' title='Horsepower' description='hp' hasValue={(query?.horsepower || 0) > 0}>
        {(!isFetching && _.isEmpty(horsepower)) && <NoOption />}
        {!_.isEmpty(horsepower) &&
          <ul className='grid grid-cols-3 sm:grid-cols-6 gap-x-2'>
            {_.map(horsepower, ({ id, name }) =>
              <li key={id}>
                <Radio name='horsepower' id={id} label={name}
                  checked={query?.horsepower?.toString() === id.toString()}
                  onChange={() => radioChanged({ name: 'horsepower', id })} />
              </li>)}
          </ul>}
      </Section>

  const Torque = () =>
    ignoreFilters?.includes('torque') ? <></> :
      <Section name='torque' title='Torque' description='ft.lbf' className='rounded-b-md' hasValue={(query?.torque || 0) > 0}>
        {(!isFetching && _.isEmpty(torque)) && <NoOption />}
        {!_.isEmpty(torque) &&
          <ul className='grid grid-cols-3 sm:grid-cols-6 gap-x-2'>
            {_.map(torque, ({ id, name }) =>
              <li key={id}>
                <Radio name='torque' id={id} label={name} checked={query?.torque?.toString() === id.toString()}
                  onChange={() => radioChanged({ name: 'torque', id })} />
              </li>)}
          </ul>}
      </Section>

  // ---- specifications ----
  const MaxSeating = () =>
    ignoreFilters?.includes('max_seating') ? <></> :
      <Section name='max_seating' title='Max Seating' hasValue={query?.max_seating}>
        {(!isFetching && _.isEmpty(max_seating)) && <NoOption />}
        {!_.isEmpty(max_seating) &&
          <ul className='grid grid-cols-4 sm:grid-cols-6 gap-x-2'>
            {_.map(max_seating, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`max_seating_${id}`} label={name}
                  checked={_.split(query?.max_seating, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'max_seating', id, val })} />
              </li>)}
          </ul>}
      </Section>

  const Doors = () => {
    if (ignoreFilters?.includes('doors')) return <></>
    const doorList = _.compact(doors)
    return (
      <Section name='doors' title='Doors' hasValue={query?.doors}>
        {(!isFetching && _.isEmpty(doors)) && <NoOption />}
        {!_.isEmpty(doorList) &&
          <ul className='grid grid-cols-2 sm:grid-cols-4 gap-x-2'>
            {_.map(doorList, ({ id, name }) =>
              <li key={id}>
                <Checkbox id={`door_${id}`} label={name}
                  checked={_.split(query?.doors, ',').includes(id.toString())}
                  onChange={(val) => checkboxChanged({ key: 'doors', id, val })} />
              </li>)}
          </ul>}
      </Section>
    )
  }

  const ExteriorColor = () =>
    <Section name='exteriorColor' title='Exterior Color' hasValue={query?.exterior_color}>
      {(!isFetching && _.isEmpty(exterior_color)) && <NoOption />}
      {!_.isEmpty(exterior_color) &&
        <ul className='grid grid-cols-2 sm:grid-cols-3 gap-2'>
          {_.map(exterior_color, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`ext_color_${id}`} label={<>
                {name}
                <div className={`v-${slugify(_.toLower(name))}`} />
              </>}
                checked={_.split(query?.exterior_color, ',').includes(id.toString())}
                onChange={(val) => checkboxChanged({ key: 'exterior_color', id, val })} />
            </li>)}
        </ul>}
    </Section>

  const InteriorColor = () =>
    <Section name='interiorColor' title='Interior Color'>
      {(!isFetching && _.isEmpty(interior_color)) && <NoOption />}
      {!_.isEmpty(interior_color) &&
        <ul className='grid grid-cols-2 sm:grid-cols-3 gap-2'>
          {_.map(interior_color, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`int_color_${id}`} label={
                <>
                  {name}
                  <div className={`v-${slugify(_.toLower(name))}`} />
                </>}
                checked={_.split(query?.interior_color, ',').includes(id.toString())}
                onChange={(val) => checkboxChanged({ key: 'interior_color', id, val })} />
            </li>)}
        </ul>}
    </Section>

  const BedLength = () =>
    ignoreFilters?.includes('bed_length') ? <></> :
      <Section name='bed_length' title='Pickup Bed Length' description='in.' hasValue={(query?.bed_length || 0) > 0}>
        {(!isFetching && _.isEmpty(bed_length)) && <NoOption />}
        {!_.isEmpty(bed_length) &&
          <ul className='grid grid-cols-3 sm:grid-cols-6 gap-x-2'>
            {_.map(bed_length, ({ id, name }) =>
              <li key={id}>
                <Radio name='bed_length' id={id} label={name}
                  checked={query?.bed_length?.toString() === id.toString()}
                  onChange={() => radioChanged({ name: 'bed_length', id })} />
              </li>)}
          </ul>}
      </Section>

  const Size = () =>
    ignoreFilters?.includes('size') ? <></> :
      <Section name='size' title='Size' hasValue={query?.size}>
        {(!isFetching && _.isEmpty(size)) && <NoOption />}
        <ul className='grid grid-cols-2 sm:grid-cols-3 gap-x-2'>
          {_.map(size, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`size_${id}`} label={name}
                checked={_.split(query?.size, ',').includes(id.toString())}
                onChange={(val) => checkboxChanged({ key: 'size', id, val })} />
            </li>)}
        </ul>
      </Section>

  const Upholstery = () =>
    ignoreFilters?.includes('upholstery') ? <></> :
      <Section name='upholstery' title='Upholstery' hasValue={query?.size}>
        {(!isFetching && _.isEmpty(upholstery)) && <NoOption />}
        <ul className='grid grid-cols-2 sm:grid-cols-4 gap-x-2'>
          {_.map(upholstery, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`upholstery_${id}`} label={name}
                checked={_.split(query?.upholstery, ',').includes(id.toString())}
                onChange={(val) => checkboxChanged({ key: 'upholstery', id, val })} />
            </li>)}
        </ul>
      </Section>

  // ---- features ----
  function featureChanged(checked: boolean, val: string) {
    const x: string[] = query?.feature ? _.split(query?.feature as string, ',') : []
    const y = checked ? _.concat(x, val) : _.without(x, val)
    setQuery({ ...query, feature: _.join(y, ',') })
  }

  const Features = () =>
    ignoreFilters?.includes('feature') ? <></> :
      <Section name='feature' title='Features' hasValue={query?.feature} className='rounded-b-md'>
        <ul className='grid grid-cols-1 sm:grid-cols-2 gap-x-2'>
          {_.map(feature, (d: string, idx: number) =>
            <li key={idx}>
              <Checkbox id={`feature_${idx}}`} label={d}
                checked={_.split(query?.feature, ',').includes(d)}
                onChange={(checked) => featureChanged(checked, d)}
              />
            </li>)}
        </ul>
      </Section>

  const Country = () =>
    ignoreFilters?.includes('assembled_in') ? <></> :
      <Section name='assembled_in' title='Assembled In' hasValue={(query?.assembled_in)}>
        {(!isFetching && _.isEmpty(country)) && <NoOption />}
        <ul className='grid grid-cols-2 sm:grid-cols-3 gap-x-2'>
          {_.map(country, ({ id, name }) =>
            <li key={id}>
              <Checkbox id={`country_${id}`} label={name}
                checked={_.split(query?.assembled_in, ',').includes(id.toString())}
                onChange={(val) => checkboxChanged({ key: 'assembled_in', id, val })} />
            </li>)}
        </ul>
      </Section>

  const Seller = () =>
    ignoreFilters?.includes('seller') ? <></> :
      <Section name='seller' title='Seller' className='rounded-b-md' hasValue={(query?.seller || 0) > 0}>
        {(!isFetching && _.isEmpty(seller)) && <NoOption />}
        {!_.isEmpty(seller) &&
          <ul className='flex flex-col md:flex-row gap-x-6'>
            {_.map(seller, ({ id, name }) =>
              <li key={id}>
                <Radio name='seller' id={id} label={name} checked={query?.seller?.toString() === id.toString()}
                  onChange={() => radioChanged({ name: 'seller', id })} />
              </li>)}
          </ul>}
      </Section>

  const Location = () =>
    ignoreFilters?.includes('location') ? <></> :
      <div>
        <div className='filter-label'>Location</div>
        <div className='flex items-start gap-4 w-fit mt-2'>
          <div className='flex items-center'>
            <label htmlFor='zip_code' className='vehicle-s2-label mr-2'>Zip Code:</label>
            <div className='w-[70px]'>
              <Text inline={true} compact={true} name='zip_code' maxLength={5} type='tel'
                value={loc?.zip_code || ''}
                onChange={e => locChanged('zip_code', e.target.value)}
                onFocus={e => e.target.select()}
              />
            </div>
          </div>
          <div className='flex items-center'>
            <div className='vehicle-s2-label mr-2'>Radius:</div>
            <div className='w-[130px]'>
              <ListBox inline={true} compact={true}
                option={radiusOption}
                value={loc?.radius || -1}
                onChange={(val) => { locChanged('radius', val) }}
              />
            </div>
          </div>
        </div>
      </div>

  const NoOption = () => <div className='text-sm text-gray-500 font-light'>No option available</div>

  return (
    <>
      <Dialog transition={true} unmount={false} open={showFilter} onClose={() => setShowFilter(false)} className='relative z-30'>
        <DialogBackdrop transition={true}
          className={twMerge('fixed inset-0 bg-zinc-800/75 transition-opacity',
            'data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in')}
        />
        <div className='fixed inset-0 z-10 w-screen overflow-y-scroll'>
          <div className='mx-auto max-w-[640px] lg:max-w-[1200px] '>
            <DialogPanel className='bg-white dark:bg-zinc-900 rounded-lg m-4 relative'>
              <PageLoading className='top-2' show={isFetching} />
              <div className='p-6 md:p-8 w-full grid grid-cols-1 lg:grid-cols-2 gap-x-10'>
                {/* 1 */}
                <div className='flex flex-col gap-y-6'>
                  {Location()}
                  <Condition />
                  <BodyType />
                  <Year />
                  {
                    !ignoreFilters?.includes('mmt') &&
                    <div className='w-full my-1'>
                      <div className='filter-label mb-3'>Make Model Trim</div>
                      <div className={groupStyle}>
                        <MakeModeTrim defaultOpen={true} name='mmt1' title='Make Model Trim 1'
                          category={category}
                          bodyTypeId={bodyTypeId}
                          makeOptions={make}
                          otherQuery={query}
                          mmt={query?.mmt1}
                          onChange={val => mmtChanged('mmt1', val)}
                        />
                        <MakeModeTrim name='mmt2' title='Make Model Trim 2'
                          category={category}
                          bodyTypeId={bodyTypeId}
                          makeOptions={make}
                          otherQuery={query}
                          mmt={query?.mmt2}
                          onChange={val => mmtChanged('mmt2', val)}
                        />
                        <MakeModeTrim name='mmt3' title='Make Model Trim 3'
                          category={category}
                          bodyTypeId={bodyTypeId}
                          makeOptions={make}
                          otherQuery={query}
                          mmt={query?.mmt3}
                          onChange={val => mmtChanged('mmt3', val)}
                          className='rounded-b-md'
                        />
                      </div>
                    </div>
                  }
                  <Mileage />
                  <Price />
                  <SaleStatus />
                  { /* toggle */}
                  <div className='space-y-5 my-4'>
                    {
                      !ignoreFilters?.includes('with_photo') &&
                      <Toggle
                        label='With Photo(s)'
                        name='with_photo' checked={_.toNumber(query?.with_photo) === 1}
                        onChange={isEnabled => toggleChanged('with_photo', isEnabled)} />
                    }
                    {
                      !ignoreFilters?.includes('with_price') &&
                      <Toggle
                        label='With Price'
                        name='with_price' checked={_.toNumber(query?.with_price) === 1}
                        onChange={isEnabled => toggleChanged('with_price', isEnabled)} />
                    }
                    {
                      !ignoreFilters?.includes('price_dropped') &&
                      <div className='pt-1'>
                        <Toggle
                          label='Recently Price Dropped '
                          name='price_dropped' checked={_.toNumber(query?.price_dropped) === 1}
                          onChange={isEnabled => toggleChanged('price_dropped', isEnabled)} />
                      </div>
                    }
                    {
                      !ignoreFilters?.includes('safest') &&
                      <div className='pb-1'>
                        <Toggle
                          label='5 Stars Safety Ratings'
                          name='safest'
                          checked={_.toNumber(query?.safest) === 1}
                          onChange={isEnabled => toggleChanged('safest', isEnabled)} />
                      </div>
                    }
                  </div>
                </div>
                {/* 2 */}
                <div className='flex flex-col gap-y-6 mt-4 lg:mt-0'>
                  {/* Powertrain */}
                  <div className='w-full'>
                    <div className='filter-section-title pb-1'>Powertrain</div>
                    <div className={groupStyle}>
                      <DriveType />
                      <FuelEconomy />
                      <FuelType />
                      <EngineAspiration />
                      <EngineCylinder />
                      <EngineDisplacement />
                      <TransType />
                      <TransGears />
                      <TowingCapacity />
                      <Acceleration />
                      <Horsepower />
                      <Torque />
                    </div>
                  </div>
                  {/* Specifications */}
                  <div className='w-full'>
                    <div className='filter-section-title pb-1'>Specifications</div>
                    <div className={groupStyle}>
                      <ExteriorColor />
                      <InteriorColor />
                      <Upholstery />
                      <Size />
                      <MaxSeating />
                      <Doors />
                      <BedLength />
                    </div>
                  </div>
                  {/* other */}
                  {
                    !ignoreFilters?.includes('other') &&
                    <div className='w-full'>
                      <div className='filter-section-title pb-1'>Other</div>
                      <div className={groupStyle}>
                        <Features />
                        <Country />
                        <Seller />
                      </div>
                    </div>
                  }
                </div>
              </div>
              {/* search button  */}
              <div className='sticky bottom-0 z-20 bg-zinc-600/10 dark:bg-zinc-800/50 rounded-b-lg px-6 py-6 border-t backdrop-blur'>
                <div className='flex justify-between items-center gap-x-2'>
                  <Button type='button' variant='transparent' onClick={() => setQuery({})} aria-label='Reset filters'>
                    <ArrowUturnLeftIcon className='size-5 mr-2' />
                    Reset
                  </Button>
                  <Button type='button' variant='primary' className='px-6 md:px-10 rounded-full' onClick={applyFilter} aria-label='Search'>
                    <MagnifyingGlassIcon className='size-5 mr-2' />
                    Search
                  </Button>
                  <Button type='button' variant='transparent' onClick={() => setShowFilter(false)} aria-label='Cancel'>
                    <XMarkIcon className='size-5 mr-2' />
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      <Button
        type='button' variant='secondary'
        className='pl-5 pr-6 rounded-full h-11 mt-4 mb-2 sm:mt-0 sm:mb-4'
        onClick={() => setShowFilter(true)} aria-label='Show Search Filters'
      >
        <MagnifyingGlassIcon className='size-5 mr-2' />
        Search Filters
      </Button>
      <button type='button'
        className={twMerge(
          'btn-float group bottom-[180px] bg-red-800 text-white hidden',
          scrollTopOver && 'lg:block'
        )}
        onClick={() => setShowFilter(true)}
        aria-label='Show Serch Filters'
      >
        <span className='flex items-center gap-x-1'>
          <MagnifyingGlassIcon className='size-7' />
          <span className='hidden group-hover:inline pr-2'>Search Filters</span>
        </span>
      </button>
      {
        !_.includes(pathname, '/vehicles/dealer') &&
        <div className={twMerge('fixed shadow-lg hidden left-0 top-0 right-0 bg-zinc-100/90 backdrop-blur-xs dark:bg-zinc-800 z-10 p-4 text-center', scrollTopOver && 'block lg:hidden')}>
          <Button type='button' variant='secondary' className='pl-4 pr-5 rounded-full h-10' onClick={() => setShowFilter(true)} aria-label='Show Search Filters'>
            <MagnifyingGlassIcon className='size-5 mr-2' />
            Search Filters
          </Button>
        </div>
      }
    </>
  )
}
